import { useMemo, useState, useEffect, useContext } from 'react'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Tooltip, Button, TextField, Typography, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import KeyOffIcon from '@mui/icons-material/KeyOff'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import KeyIcon from '@mui/icons-material/Key'

import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { addApiKey, deleteApiKey, setApiKeyStatus, toggleApiKeyClientFunctionAccess } from '../../providers/clients-api'
import { getApiKeys, enableApiAccess, disableApiAccess, abortDisableApiAccess } from '../../providers/studio-api'

import DataExportDialog from '/components/data-export-dialog'
import ClickToCopy from '/components/click-to-copy'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import excelExport from './key-list-to-excel'
import { confirm } from '/components/confim-dialog'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const defaultApiAccess = {
  enabled: false,
  billingMode: "",
  tariffName: "",
  monthlyFeeCredits: 0,
  proRataCredits: 0,
  currentCredits: 0,
  insufficientCredits: false,
  disabledRequested: false,
  inFreeAccessPeriod: false,
  nextBillingDate: ""
}

const APIKeyAdmin = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const { apiSource, apiKey, setProcessing, selectedClientId } = useContext(AppContext)

  const [loading, setLoading] = useState(true)
  const [keys, setKeys] = useState([])
  const [apiAccess, setApiAccess] = useState(defaultApiAccess)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [keyName, setKeyName] = useState("")
  const [confirmBillingFee, setConfirmBillingFee] = useState(false)
  const [confirmDisableOnLowCredits, setConfirmDisableOnLowCredits] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [confirmDisableAccess, setConfirmDisableAccess] = useState(false)
  const [abortDisableDialogOpen, setAbortDisableDialogOpen] = useState(false)
  const [confirmAbortBillingFee, setConfirmAbortBillingFee] = useState(false)
  const [confirmAbortDisableOnLowCredits, setConfirmAbortDisableOnLowCredits] = useState(false)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  useEffect(() => {
    getKeys()
  }, [selectedClientId])

  const getKeys = async () => {
    setLoading(true)
    const result = await getApiKeys({ apiSource, apiKey })
    setLoading(false)

    if (result.ok) {
      setKeys(result.keys || [])
      setApiAccess(result.apiAccess || defaultApiAccess)
      setConfirmBillingFee(false)
      setConfirmDisableOnLowCredits(false)
      setConfirmDisableAccess(false)
      setConfirmAbortBillingFee(false)
      setConfirmAbortDisableOnLowCredits(false)
    }
  }

  const addKey = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding new API key', variant: 'light' })
    const result = await addApiKey({ apiSource, apiKey, keyName })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API Key added', variant: 'success' })
      setKeys(result.keys)
      setKeyName("")
    } else {
      showActionError(result.error, 'Error adding API key')
    }
  }

  const deleteKey = async keyId => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this key rather than temporarily disable it?'
    })

    if (confirmed) {
      setProcessing(true)
      enqueueSnackbar({ message: 'Removing API Key', variant: 'light' })
      const result = await deleteApiKey({ apiSource, apiKey, keyId })
      setProcessing(false)

      if (result.ok) {
        enqueueSnackbar({ message: 'API Key removed', variant: 'success' })
        setKeys(result.keys)
      } else {
        showActionError(result.error, 'Error removing API key')
      }
    }
  }

  const toggleKeyEnabled = async keyId => {
    const keyRecord = keys.find(u => u.user_id === keyId)
    const status = 1 - keyRecord.user_enabled

    setProcessing(true)
    enqueueSnackbar({ message: 'Setting key status', variant: 'light' })
    const result = await setApiKeyStatus({ apiSource, apiKey, keyId, status })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'Key status saved', variant: 'success' })
      setKeys(result.keys)
    } else {
      showActionError(result.error, 'Error setting API key status')
    }
  }

  const toggleClientFunctionAccess = async keyId => {
    const result = await toggleApiKeyClientFunctionAccess({ apiSource, apiKey, keyId })
    if (result.ok) {
      setKeys(result.keys)
    }
  }

  const handleEnableApiAccess = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Enabling API access', variant: 'light' })
    const result = await enableApiAccess({ apiSource, apiKey })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API access enabled', variant: 'success' })
      setKeys(result.keys || [])
      setApiAccess(result.apiAccess || defaultApiAccess)
      setConfirmBillingFee(false)
      setConfirmDisableOnLowCredits(false)
    } else {
      showActionError(result.error, 'Error enabling API access')
    }
  }

  const handleDisableApiAccess = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Scheduling API access to be disabled', variant: 'light' })
    const result = await disableApiAccess({ apiSource, apiKey })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API access will be disabled at the next billing date', variant: 'success' })
      setKeys(result.keys || [])
      setApiAccess(result.apiAccess || defaultApiAccess)
      setDisableDialogOpen(false)
      setConfirmDisableAccess(false)
    } else {
      showActionError(result.error, 'Error disabling API access')
    }
  }

  const handleAbortDisableApiAccess = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: abortSnackbarMessage, variant: 'light' })
    const result = await abortDisableApiAccess({ apiSource, apiKey })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: abortSuccessMessage, variant: 'success' })
      setKeys(result.keys || [])
      setApiAccess(result.apiAccess || defaultApiAccess)
      setAbortDisableDialogOpen(false)
      setConfirmAbortBillingFee(false)
      setConfirmAbortDisableOnLowCredits(false)
    } else {
      showActionError(result.error, 'Error aborting API access cancellation')
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'user_id',
      header: 'Key ID',
      filterVariant: 'autocomplete',
      size: 80,
    },
    {
      accessorKey: 'username',
      header: 'Key name',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="Key name copied">{cell.getValue()}</ClickToCopy>,
    },
    {
      accessorKey: 'user_enabled',
      header: 'Enabled',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'allow_access_to_client_functions',
      header: 'Client Function Access',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'key',
      header: 'API Key',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="API key copied">{cell.getValue()}</ClickToCopy>,
    },
  ], [])

  const table = useMaterialReactTable({
    columns,
    data: keys,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: originalRow => originalRow.user_id,
    ...tableStyles,
    renderRowActionMenuItems: ({ closeMenu, row }) => getActions(toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, closeMenu, row),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-start gap-5'>
        <TextField value={keyName} onChange={e => setKeyName(e.target.value)} label="new key name" slotProps={{ inputLabel: { shrink: true } }} />
        <Button startIcon={<KeyIcon className='mr-2' />} onClick={addKey} disabled={keyName.length < 3} variant='contained'>Add Api Key</Button>
        {apiAccess.billingMode === "prepay" ? (
          <Button color='error' variant='contained' onClick={() => setDisableDialogOpen(true)} disabled={apiAccess.disabledRequested}>
            Disable API Access
          </Button>
        ) : null}
      </div>
    )
  })

  const nextBillingMoment = apiAccess.nextBillingDate ? moment(apiAccess.nextBillingDate, "YYYYMMDD") : null
  const billingDateShort = nextBillingMoment ? nextBillingMoment.format("DD MMM") : ""
  const disableDateLong = nextBillingMoment ? nextBillingMoment.format("DD MMM YYYY") : ""
  const showEnablePanel = apiAccess.billingMode === "prepay" && !apiAccess.enabled
  const canEnable = confirmBillingFee && confirmDisableOnLowCredits && !apiAccess.insufficientCredits
  const continueApiAccess = apiAccess.inFreeAccessPeriod
  const abortButtonLabel = continueApiAccess ? "Continue API Access" : "Abort cancellation"
  const abortDialogTitle = continueApiAccess ? "Continue API access" : "Abort API access cancellation"
  const abortDialogMessage = continueApiAccess
    ? `Your free API access period ends on ${disableDateLong}. Continuing will move you onto paid API access from that date.`
    : `API access will continue beyond ${disableDateLong} if you abort this cancellation request.`
  const abortSnackbarMessage = continueApiAccess ? 'Continuing API access' : 'Aborting API access cancellation'
  const abortSuccessMessage = continueApiAccess ? 'API access will continue after the free period' : 'API access cancellation aborted'
  const tableHeight = apiAccess.disabledRequested ? 'calc(100vh - 230px)' : 'calc(100vh - 185px)'

  return (
    <>
      <Dialog
        open={disableDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return
          setDisableDialogOpen(false)
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Disable API access</DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-4 min-w-[420px] pt-4'>
            <Typography>
              API access will remain active until your next billing period starts on {disableDateLong}.
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={confirmDisableAccess} onChange={e => setConfirmDisableAccess(e.target.checked)} />}
              label={`I wish API access to be disabled from ${disableDateLong}.`}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDisableApiAccess} disabled={!confirmDisableAccess}>Disable API Access</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={abortDisableDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return
          setAbortDisableDialogOpen(false)
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>{abortDialogTitle}</DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-4 min-w-[420px] pt-4'>
            <Typography>
              {abortDialogMessage}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={confirmAbortBillingFee} onChange={e => setConfirmAbortBillingFee(e.target.checked)} />}
              label={`I agree to the monthly API access fee of ${apiAccess.monthlyFeeCredits.toLocaleString()} credits, charged each month on ${billingDateShort}.`}
            />
            <FormControlLabel
              control={<Checkbox checked={confirmAbortDisableOnLowCredits} onChange={e => setConfirmAbortDisableOnLowCredits(e.target.checked)} />}
              label='I understand that API access will be disabled if there are insufficient credits on the billing date.'
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbortDisableDialogOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleAbortDisableApiAccess}
            disabled={!confirmAbortBillingFee || !confirmAbortDisableOnLowCredits}
          >
            {abortButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {showEnablePanel ? (
        <StyledPaper>
          <div className='flex flex-col gap-4 p-5 max-w-[900px]'>
            <Typography variant='h5'>API access is currently disabled</Typography>
            <Typography>
              API access is a paid option on prepay tariffs. Enabling API access costs {apiAccess.monthlyFeeCredits.toLocaleString()} credits per month and will be charged on your billing date of {billingDateShort}.
            </Typography>
            <Typography>
              A pro-rata fee of {apiAccess.proRataCredits.toLocaleString()} credits will be deducted immediately to cover the period up to your next billing date.
            </Typography>
            <Typography>
              Current credit balance: {apiAccess.currentCredits.toLocaleString()} credits.
            </Typography>

            {apiAccess.insufficientCredits ? (
              <Alert severity='error'>
                You do not have enough credits to enable API access right now. Please <Link to='/billing/buy-credits' className='link'>buy more credits</Link> before continuing.
              </Alert>
            ) : null}

            <FormControlLabel
              control={<Checkbox checked={confirmBillingFee} onChange={e => setConfirmBillingFee(e.target.checked)} />}
              label={`I agree to the monthly API access fee of ${apiAccess.monthlyFeeCredits.toLocaleString()} credits, charged each month on ${billingDateShort}.`}
            />
            <FormControlLabel
              control={<Checkbox checked={confirmDisableOnLowCredits} onChange={e => setConfirmDisableOnLowCredits(e.target.checked)} />}
              label='I understand that API access will be disabled if there are insufficient credits on the billing date.'
            />

            <div className='flex flex-row justify-end'>
              <Button variant='contained' onClick={handleEnableApiAccess} disabled={!canEnable}>
                Enable API Access
              </Button>
            </div>
          </div>
        </StyledPaper>
      ) : (
        <>
          {apiAccess.disabledRequested ? (
            <Alert
              severity='warning'
              sx={{ mb: 2 }}
              action={
                <Button color='inherit' size='small' variant='outlined' onClick={() => setAbortDisableDialogOpen(true)}>
                  {abortButtonLabel}
                </Button>
              }
            >
              API access is scheduled to be disabled on {disableDateLong}.
            </Alert>
          ) : null}
          <Box sx={{ height: tableHeight, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
              <MaterialReactTable table={table} />
            </StyledPaper>
          </Box>
        </>
      )}

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="api_keys" data={keys} excelExport={excelExport} />
      <ActionErrorDialog title="API key action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default APIKeyAdmin

const getActions = (toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, closeMenu, row) => {
  const actions = [
    <MenuItem key="toggle" onClick={() => { toggleKeyEnabled(row.original.user_id); closeMenu() }}><KeyIcon className='mr-2' />{row.original.user_enabled === 1 ? "Disable" : "Enable"} Key</MenuItem>,
    <MenuItem key="admin" onClick={() => { toggleClientFunctionAccess(row.original.user_id); closeMenu() }}><SupervisorAccountIcon className='mr-2' />{row.original.allow_access_to_client_functions === 1 ? "Disable" : "Enable"} Client Function Access for Key</MenuItem>,
    <MenuItem key="delete" onClick={() => { deleteKey(row.original.user_id); closeMenu() }}><KeyOffIcon className='mr-2' /> Delete Key</MenuItem>,
  ]

  return actions
}
